import axios from "axios";

// http://localhost:8000/api/
const apiClient = (baseURL = "/api", timeout = 60000) => {
    const options = {
        baseURL: baseURL,
        timeout: timeout,
        headers: {
            "Content-Type": "application/json"
        }
    }

    const client = axios.create(options);

    client.interceptors.request.use(
        (config) => {
            const accessToken = localStorage.access;
            if (accessToken) {
                config.headers["Authorization"] = `Bearer ${accessToken}`
            }
            return config;
        },
        (requestError) => {
            return Promise.reject(requestError)
        }
    )

    client.interceptors.response.use(
        (response) => response,
        (error) => {
            const config = error.config;
            if (error.response.status === 401) { // If unauthorized request, attempt to refresh our user's token.
                console.log('unauth request!')

                if (localStorage.getItem("refresh") === null) {
                    return Promise.reject(error);
                }

                if (error.config.url === '/token/refresh') {
                    // Unauthorized error encountered while refreshing token, must mean our refresh token has also expired.
                    // Force user re-authentication.
                    localStorage.removeItem('access');
                    localStorage.removeItem('refresh');

                    // Resolve promise when errors encountered on this url because it prevents continuous error loops.
                    return Promise.resolve(error);
                }

                return client.post("/token/refresh", { "refresh": localStorage.refresh }).then((response) => {
                    if (response.data && response.data.access) {
                        localStorage.access = response.data.access;
                    }
                    return client(config);
                }).catch((tokenRefreshError) => {
                    console.log('did this happen?')
                    localStorage.removeItem('access');
                    localStorage.removeItem('refresh');
                    return Promise.reject(tokenRefreshError);
                });
            }
            return Promise.reject(error);
        }
    );

    return client;
}

class APIClient {
    constructor(baseURL = '/api', timeout=30000) {
        this.client = apiClient(baseURL, timeout)
    }

    /** Authenticate provided username and password against server to obtain JWT access and refresh tokens. */
    authenticate(username, password) {
        return this.client.post('/token', {
            "username": username,
            "password": password
        }).then(response => Promise.resolve(response)).catch(error => {
            return Promise.reject(error)
        })
    }

    /** Obtain a new access token using our user's refresh token. */
    refreshToken(refreshToken) {
        return this.client.post('/token/refresh', {
            "refreshToken": refreshToken
        }).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getAuthenticatedUser() {
        return this.client.get('/user').then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postUser(payload) {
        return this.client.post('/users', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postRequestPasswordReset(payload) {
        return this.client.post('/request-password-reset', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postResetPassword(payload) {
        return this.client.post('/reset-password', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getCreditProducts() {
        return this.client.get('/store/credits/products').then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getStoreCategories() {
        return this.client.get('/store/category').then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getStoreProducts() {
        return this.client.get('/store/products/all').then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getStoreProducts(category, page) {
        return this.client.get(`/store/products/${category}/?page=${page}`).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getStoreCreditAmount() {
        return this.client.get(`/store/credits/amount`).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getStoreProductsForCategories(categories) {
        let queryParams = ''
        for (let category of categories) {
            if (queryParams.length === 0) {
                queryParams += '?category='
            } else {
                queryParams += '&category='
            }
            queryParams += category
        }
        return this.client.get(`/store/products${queryParams}`).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postStorePurchase(payload) {
        return this.client.post('/store/purchase', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postCreditPurchase(payload) {
        return this.client.post('/store/credits/purchase', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getTickets() {
        return this.client.get('/tickets').then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getTicket(id) {
        return this.client.get('/tickets' + id + '').then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postTicket(payload) {
        return this.client.post('/tickets', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    patchTicket(id, payload) {
        return this.client.patch('/tickets' + id + '', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postComment(payload) {
        return this.client.post('/tickets/comments', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getHiscores(page) {
        return this.client.get(`/hiscores/?page=${page}`).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getPlayerHiscores(username) {
        return this.client.get('/hiscores/player/' + username +'').then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getForums() {
        return this.client.get(`/forums/`).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getCategories() {
        return this.client.get(`/forums/categories/`).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getForumThreads(forum, page) {
        return this.client.get(`/forums/threads/${forum}?page=${page}`).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getForumThread(id, page) {
        return this.client.get(`/forums/thread/${id}/?page=${page}`).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postForumThread(payload) {
        return this.client.post('/forums/threads', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postForumPost(payload) {
        return this.client.post('/forums/posts', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getLatestForumThreads(amount) {
        return this.client.get(`/forums/threads/latest?amount=${amount}`).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getLatestNews(amount) {
        return this.client.get(`/news/latest?amount=${amount}`).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getNews(year, month) {
        return this.client.get(`/news/?year=${year}&month=${month}`,).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getStorePurchases(type) {
        return this.client.get(`/store/purchases`,).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postPasswordChange(payload) {
        return this.client.post('/change-password', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getTwoFactorStatus() {
        return this.client.get('/2fa/status').then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postTwoFactorDeactivation(payload) {
        return this.client.post('/2fa/deactivate', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postTwoFactorActivation(payload) {
        return this.client.post('/2fa/activate', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getVoteCountdowns() {
        return this.client.get('/vote/countdown').then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getVoteStatuses() {
        return this.client.get('/vote/statuses').then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getVoteMetrics() {
        return this.client.get('/vote/metrics').then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getNews(slug) {
        return this.client.get(`/news/${slug}/`).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postNews(payload) {
        return this.client.post(`/news/`, payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }
}

export default APIClient
